import React from 'react';
import { AlertTriangle, Info, CheckCircle } from 'lucide-react';

const iconMap = {
  danger: { icon: AlertTriangle, bg: 'bg-red-50', color: 'text-red-500' },
  warning: { icon: AlertTriangle, bg: 'bg-amber-50', color: 'text-amber-500' },
  info: { icon: Info, bg: 'bg-brand-50', color: 'text-brand-500' },
  success: { icon: CheckCircle, bg: 'bg-emerald-50', color: 'text-emerald-500' }
};

const buttonStyles = {
  danger: 'bg-red-600 hover:bg-red-700',
  warning: 'bg-amber-600 hover:bg-amber-700',
  info: 'bg-brand-600 hover:bg-brand-700',
  success: 'bg-emerald-600 hover:bg-emerald-700'
};

export default function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  isLoading = false
}) {
  if (!isOpen) return null;

  const { icon: Icon, bg, color } = iconMap[variant] || iconMap.danger;

  /** Purpose: Handles confirm. Inputs: none. Output: Operation result. */
  const handleConfirm = () => {
    onConfirm();
  };

  /** Purpose: Handles key down. Inputs: e. Output: Operation result. */
  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && !isLoading) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 modal-backdrop flex items-center justify-center p-4 z-50"
      onClick={(e) => e.target === e.currentTarget && !isLoading && onClose()}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirmation-title"
      aria-describedby="confirmation-message"
    >
      <div className="bg-white rounded-3xl shadow-elevated max-w-md w-full overflow-hidden animate-scale-in">
        <div className="p-7">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-2xl ${bg} flex-shrink-0`}>
              <Icon className={`w-5 h-5 ${color}`} />
            </div>

            <div className="flex-1 min-w-0">
              <h3
                id="confirmation-title"
                className="text-lg font-bold text-slate-900 tracking-tight"
              >
                {title}
              </h3>
              <p
                id="confirmation-message"
                className="mt-2 text-sm text-slate-500 leading-relaxed"
              >
                {message}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-slate-50/50 px-7 py-4 flex gap-3 justify-end border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2.5 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isLoading}
            className={`px-5 py-2.5 text-sm font-medium text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 ${buttonStyles[variant]}`}
          >
            {isLoading && (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
